import type { BookingPro, BookingService, VerifiedCustomer } from './booking-api';
import {
  Calendar,
  Check,
  ChevronDown,
  ChevronRight,
  Clock,
  Loader2,
  MessageSquare,
  Phone,
} from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { EMPTY_FORM_DATA, fieldsFromData, missingRequiredFromData, noteConfigFromData, stepTextFromData } from '../../forms/form-data';
import { createBooking, fetchBookingPublic, sendOtp, uploadFormImage, verifyOtp } from './booking-api';
import { fieldValueText, parkedBookingPayload } from './booking-fallback';
import { formatPhone, nextPhoneInput, phoneHref, toE164 } from "@servicepro/utils";
import type { BaseComponentProps } from '../../types/puck';
import { Button } from "../ui/button";
import type { Data } from '@puckeditor/core';
import type { FieldValue } from '../../forms/form-data';
import { FormEmbedSkeleton } from "../../forms/form-embed";
import { FormValuesProvider } from "../../forms/form-values-context";
import { Input } from "../ui/input";
import type { JobsiteBusinessContext } from '../../types/runtime-config';
import type { ParkedBookingInput } from './booking-fallback';
import React from "react";
import { Render } from '@puckeditor/core';
import { Textarea } from "../ui/textarea";
import { cn } from "../../lib/utils";
import { formsPuckConfig } from "../../forms/form-fields.config";
import { getWindowJobsiteConfig } from '../../types/runtime-config';
import { submitLead } from "../../forms/submit-lead";
import { useTurnstile } from "../../hooks/use-turnstile";

interface RequestDay {
  iso: string; // YYYY-MM-DD
  dow: string; // "Wed"
  day: number; // 18
  label: string; // "Today" | "Tomorrow" | "Wed, Jun 18"
}

type WindowId = "asap" | "morning" | "afternoon" | "evening";

interface WindowOption {
  id: WindowId;
  label: string;
  hint: string;
}

type LoadState = "loading" | "ready" | "unavailable" | "offline";

/** Per-step header overrides, as the form builder stores them. */
type StepText = ReturnType<typeof stepTextFromData>;

interface BookingWidgetInlineProps {
  slug?: string;
  apiUrl?: string;
  businessName?: string;
  businessPhone?: string;
  /** Heading shown above the service picker. */
  title?: string;
  /** Optional supporting line under the title (most steps supply their own). */
  subtitle?: string;
  className?: string;
  /** Dashboard preview: render the real flow from a draft form (no network) so
   *  the form builder shows the whole widget — picker first, then custom fields. */
  preview?: { formData: Data; services?: BookingService[] };
  /** Form-builder mode: the host drives navigation, gating is relaxed, and the
   *  step buttons advance without hitting the network (so the whole flow is
   *  clickable as a preview). The Details step renders `renderFields()`. */
  editor?: boolean;
  /** Slot for the Details-step field area. The builder passes an editable Puck
   *  canvas here; live surfaces leave it unset and get the saved <Render>. */
  renderFields?: () => React.ReactNode;
  /** Controlled current step (editor). When set, the host owns navigation. */
  step?: Step;
  onStepChange?: (step: Step) => void;
}

type Step = "select" | "details" | "phone" | "otp" | "profile" | "review" | "done";

/** Public alias + ordered metadata so the form builder can render a step
 *  navigator that mirrors the live wizard exactly. */
type BookingStep = Step;

interface BookingWidgetProps extends BaseComponentProps {
  heading?: string;
  subheading?: string;
  /** Resolved by the Puck config from business context / window config. */
  slug?: string;
  apiUrl?: string;
  businessName?: string;
  businessPhone?: string;
  backgroundColor?: string;
}

/* ────────────────────────── helpers ────────────────────────── */

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
  MON = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ],
  /**
   * Analytics session, unless this is an editor preview (which must not attribute).
   * @returns {string | null} The visitor's session id, or null when there is none to attribute.
   */
  currentSessionId = (): string | null => {
    if (typeof window === "undefined") {
      return null;
    }
    const isPreview = new URLSearchParams(window.location.search).get("preview") === "true";
    if (isPreview) {
      return null;
    }
    return (sessionStorage.getItem("sp_session_id") ?? "") || null;
  },
  OTP_LENGTH = 6,
  // Lets the step's enter transition settle before the keyboard opens.
  OTP_FOCUS_DELAY_MS = 120,
  OTP_SLOTS = Array.from({ length: OTP_LENGTH }, (_slot, index) => index),
  /**
   * A string that holds something: not absent, not empty.
   * @param {string | null | undefined} value - The string to test.
   * @returns {boolean} True when the value is a non-empty string.
   */
  hasText = (value: string | null | undefined): value is string => typeof value === "string" && value !== "",
  /**
   * How the rail names an offered day.
   * @param {Date} date - The offered day.
   * @param {number} offset - Days from today, 0 for today.
   * @returns {string} "Today", "Tomorrow", else the weekday and date ("Wed, Jun 18").
   */
  requestDayLabel = (date: Date, offset: number): string => {
    if (offset === 0) {
      return "Today";
    }
    if (offset === 1) {
      return "Tomorrow";
    }
    return `${DOW[date.getDay()]}, ${MON[date.getMonth()]} ${date.getDate()}`;
  },
  /**
   * The calendar day a number of days after another.
   * @param {Date} base - The day to count from.
   * @param {number} offset - How many days later.
   * @returns {Date} A new date, leaving `base` untouched.
   */
  addDays = (base: Date, offset: number): Date => {
    const date = new Date(base);
    date.setDate(base.getDate() + offset);
    return date;
  },
  /**
   * 14 selectable days starting today. Request-mode: every day is open (no
   * availability lookup) — the pro confirms the exact time afterward.
   * @returns {RequestDay[]} The days to offer, today first.
   */
  buildRequestDays = (): RequestDay[] => {
    const base = new Date();
    base.setHours(0, 0, 0, 0);
    return Array.from({ length: 14 }, (_day, offset) => {
      const date = addDays(base, offset),
        yyyy = date.getFullYear(),
        mm = `${date.getMonth() + 1}`.padStart(2, "0"),
        dd = `${date.getDate()}`.padStart(2, "0");
      return {
        day: date.getDate(),
        dow: DOW[date.getDay()],
        iso: `${yyyy}-${mm}-${dd}`,
        label: requestDayLabel(date, offset),
      };
    });
  },
  WINDOWS: WindowOption[] = [
    { hint: "I'm flexible", id: "asap", label: "Soonest available" },
    { hint: "8am – 12pm", id: "morning", label: "Morning" },
    { hint: "12pm – 5pm", id: "afternoon", label: "Afternoon" },
    { hint: "5pm – 8pm", id: "evening", label: "Evening" },
  ],
  money = (amount: number): string => `$${amount}`,
  /**
   * The qualifier in front of a price ("From "), with its trailing space.
   * @param {string | null} qualifier - The pro's qualifier, if any.
   * @returns {string} The qualifier and a space, or "" when there is none.
   */
  qualifierPrefix = (qualifier: string | null): string => {
    if (!hasText(qualifier)) {
      return "";
    }
    return `${qualifier} `;
  },
  /**
   * Price chip — empty when the pro hasn't set a price (we don't want "Estimate"
   * plastered across every service; absence reads cleaner than a filler word).
   * @param {BookingService} service - The service whose price to show.
   * @returns {string} The qualifier and price, or "" when no price is set.
   */
  priceLabel = (service: BookingService): string => {
    if (service.price != null) {
      return `${qualifierPrefix(service.priceQualifier)}${money(service.price)}`;
    }
    return "";
  },
  /**
   * A service as the picker lists it.
   * @param {BookingService} service - The service to list.
   * @returns {string} Its name, followed by its price when it has one.
   */
  serviceOptionLabel = (service: BookingService): string => {
    const price = priceLabel(service);
    if (price === "") {
      return service.name;
    }
    return `${service.name} · ${price}`;
  },
  /**
   * The load state after the booking API turned the widget away. A refusal is this
   * business's configuration; only an outage may capture.
   * @param {boolean} offline - Whether the API was unreachable rather than refusing.
   * @param {boolean} canPark - Whether this site has somewhere to hold a request.
   * @returns {LoadState} "offline" to ask in free text, else "unavailable".
   */
  failedLoadState = (offline: boolean, canPark: boolean): LoadState => {
    if (offline && canPark) {
      return "offline";
    }
    return "unavailable";
  },
  /**
   * The Turnstile field a parked request carries, when there is a token.
   * @param {string | undefined} token - The solved Turnstile token, if any.
   * @returns {{ turnstileToken?: string }} The field to spread into the payload.
   */
  turnstileField = (token: string | undefined): { turnstileToken?: string } => {
    if (hasText(token)) {
      return { turnstileToken: token };
    }
    return {};
  },
  /**
   * The custom field answers to send with a booking.
   * @param {Record<string, FieldValue>} values - Answers keyed by field.
   * @returns {Record<string, FieldValue> | undefined} The answers, or undefined when there are none.
   */
  customFieldsToSend = (values: Record<string, FieldValue>): Record<string, FieldValue> | undefined => {
    if (Object.keys(values).length === 0) {
      return undefined;
    }
    return values;
  },
  /**
   * Who is booking: the verified customer's name when they are known, else what they typed.
   * @param {VerifiedCustomer | undefined} customer - The customer the code verified, if any.
   * @param {{ first: string; last: string }} profile - The name typed on the profile step.
   * @returns {string} The full name, "" when there is none.
   */
  displayName = (customer: VerifiedCustomer | undefined, profile: { first: string; last: string }): string => {
    if (customer?.exists === true) {
      return `${customer.firstName ?? ""} ${customer.lastName ?? ""}`.trim();
    }
    return `${profile.first} ${profile.last}`.trim();
  },
  /**
   * A value to show, or the form builder's sample while the real one is still blank.
   * @param {string} value - The visitor's value.
   * @param {boolean | undefined} editor - Whether this is the form builder.
   * @param {string} sample - What the builder shows in its place.
   * @returns {string} The value, else the sample in the builder, else "".
   */
  orSample = (value: string, editor: boolean | undefined, sample: string): string => {
    if (value !== "") {
      return value;
    }
    if (editor === true) {
      return sample;
    }
    return "";
  },
  /**
   * The unavailable card's line, which only promises a call when there is a number.
   * @param {boolean} hasPhone - Whether there is a number to call.
   * @returns {string} The line under the title.
   */
  unavailableMessage = (hasPhone: boolean): string => {
    if (hasPhone) {
      return "Reach out and we'll get you scheduled.";
    }
    return "Get in touch and we'll get you scheduled.";
  },
  /**
   * The error when a parked request could not be sent either.
   * @param {string | null} phoneNumber - The business's number, if it has one.
   * @returns {string} The message, pointing at the phone when there is one.
   */
  sendFailedMessage = (phoneNumber: string | null): string => {
    if (hasText(phoneNumber)) {
      return `That didn't send. Please call ${formatPhone(phoneNumber)}.`;
    }
    return "That didn't send. Please try again shortly.";
  },
  /**
   * The success card's title.
   * @param {boolean} parked - Whether the request became a lead to schedule by hand.
   * @param {Data} formData - The form, which may override the title.
   * @returns {string} The title.
   */
  doneTitle = (parked: boolean, formData: Data): string => {
    if (parked) {
      return "Request received.";
    }
    return (stepTextFromData(formData).done?.title?.trim() ?? "") || "Request sent.";
  },
  /**
   * The success card's line about what happens next.
   * @param {boolean} parked - Whether the request became a lead to schedule by hand.
   * @param {string} who - The business, as the line names it.
   * @param {string} firstName - The visitor's first name, "" when not given.
   * @returns {string} The line.
   */
  doneMessage = (parked: boolean, who: string, firstName: string): string => {
    if (parked) {
      return `${who} couldn't confirm a time just now, so they'll call you to schedule.`;
    }
    return `${who} will text ${firstName || "you"} to confirm a time.`;
  },
  /**
   * The step the select step's Continue goes to.
   * @param {Data} formData - The form, whose custom fields get their own step.
   * @returns {Step} "details" when the form has custom fields, else "phone".
   */
  afterSelectStep = (formData: Data): Step => {
    if (fieldsFromData(formData).length > 0) {
      return "details";
    }
    return "phone";
  },
  /**
   * The step the phone step's Back goes to.
   * @param {Data} formData - The form, whose custom fields get their own step.
   * @returns {Step} "details" when the form has custom fields, else "select".
   */
  beforePhoneStep = (formData: Data): Step => {
    if (fieldsFromData(formData).length > 0) {
      return "details";
    }
    return "select";
  },
  /**
   * The step the review step's Back goes to.
   * @param {boolean} known - Whether the customer was already on file (and skipped the profile).
   * @returns {Step} "select" for a known customer, else "profile".
   */
  reviewBackStep = (known: boolean): Step => {
    if (known) {
      return "select";
    }
    return "profile";
  },
  /**
   * The note box's placeholder.
   * @param {boolean} required - Whether the form requires the note.
   * @param {string} label - The note's label.
   * @returns {string} The label, marked optional when the note is.
   */
  notePlaceholder = (required: boolean, label: string): string => {
    if (required) {
      return label;
    }
    return `${label} (optional)`;
  },
  /**
   * A step's title: the form builder's override when it has text, else the default.
   * @param {StepText} stepText - The form's per-step overrides.
   * @param {Step} step - The step to title.
   * @param {string} fallback - The default title.
   * @returns {string} The title to show.
   */
  overrideTitle = (stepText: StepText, step: Step, fallback: string): string => {
    const override = stepText[step]?.title ?? "";
    if (override.trim() !== "") {
      return override;
    }
    return fallback;
  },
  /**
   * A step's subtitle: the form builder's override when it set one (even blank), else the default.
   * @param {StepText} stepText - The form's per-step overrides.
   * @param {Step} step - The step to subtitle.
   * @param {string} fallback - The default subtitle.
   * @returns {string} The subtitle to show.
   */
  overrideSubtitle = (stepText: StepText, step: Step, fallback: string): string => {
    const override = stepText[step]?.subtitle;
    if (override === undefined) {
      return fallback;
    }
    return override;
  },
  /**
   * A button's label, swapped for a spinner while its request is in flight.
   * @param {boolean} busy - Whether the request is in flight.
   * @param {string} label - The label when idle.
   * @returns {React.ReactNode} The spinner or the label.
   */
  busyLabel = (busy: boolean, label: string): React.ReactNode => {
    if (busy) {
      return <Loader2 className="jobsite:h-4 jobsite:w-4 jobsite:animate-spin" />;
    }
    return label;
  },
  /**
   * The short name over a day in the rail.
   * @param {RequestDay} day - The offered day.
   * @returns {string} "Today" for today, else the weekday ("Wed").
   */
  railDayName = (day: RequestDay): string => {
    if (day.label === "Today") {
      return "Today";
    }
    return day.dow;
  },
  UPPER_LABEL =
    "jobsite:text-[11px] jobsite:font-bold jobsite:uppercase jobsite:tracking-[0.08em] jobsite:text-muted-foreground",
  renderDayButton = (
    day: RequestDay,
    selected: boolean,
    onSelect: (iso: string) => void,
  ): React.JSX.Element => (
    <button
      key={day.iso}
      type="button"
      onClick={() => {
        onSelect(day.iso);
      }}
      className="jobsite:flex jobsite:w-[44px] jobsite:shrink-0 jobsite:flex-col jobsite:items-center jobsite:gap-1.5"
    >
      <span className="jobsite:text-[11px] jobsite:font-semibold jobsite:uppercase jobsite:text-muted-foreground">
        {railDayName(day)}
      </span>
      <span
        className={cn(
          "jobsite:flex jobsite:h-[42px] jobsite:w-[42px] jobsite:items-center jobsite:justify-center jobsite:rounded-full jobsite:border jobsite:font-heading jobsite:text-base jobsite:transition-colors",
          selected && "jobsite:border-primary jobsite:bg-primary jobsite:text-primary-foreground",
          !selected && "jobsite:border-border jobsite:text-foreground jobsite:hover:border-primary",
        )}
      >
        {day.day}
      </span>
    </button>
  ),
  DayRail = ({
    days,
    selectedIso,
    onSelect,
  }: {
    days: RequestDay[];
    selectedIso: string;
    onSelect: (iso: string) => void;
  }): React.JSX.Element => (
    <div>
      <div className={UPPER_LABEL}>Pick a day</div>
      <div className="jobsite:mt-2.5 jobsite:flex jobsite:gap-1.5 jobsite:overflow-x-auto jobsite:pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {days.map((day) => renderDayButton(day, day.iso === selectedIso, onSelect))}
      </div>
    </div>
  ),
  renderWindowButton = (
    option: WindowOption,
    selected: boolean,
    onSelect: (windowId: WindowId) => void,
  ): React.JSX.Element => (
    <button
      key={option.id}
      type="button"
      onClick={() => {
        onSelect(option.id);
      }}
      className={cn(
        "jobsite:rounded-xl jobsite:border jobsite:px-3.5 jobsite:py-3 jobsite:text-left jobsite:transition-colors",
        selected && "jobsite:border-primary jobsite:bg-primary/10",
        !selected && "jobsite:border-border jobsite:bg-background jobsite:hover:border-primary/50",
      )}
    >
      <span
        className={cn(
          "jobsite:block jobsite:text-[15px] jobsite:font-semibold",
          selected && "jobsite:text-primary",
          !selected && "jobsite:text-foreground",
        )}
      >
        {option.label}
      </span>
      <span className="jobsite:mt-0.5 jobsite:block jobsite:text-xs jobsite:text-muted-foreground">
        {option.hint}
      </span>
    </button>
  ),
  WindowGrid = ({
    value,
    onSelect,
  }: {
    value: WindowId | null;
    onSelect: (windowId: WindowId) => void;
  }): React.JSX.Element => (
    <div>
      <div className={UPPER_LABEL}>Preferred time</div>
      <div className="jobsite:mt-2.5 jobsite:grid jobsite:grid-cols-1 jobsite:gap-2.5 jobsite:sm:grid-cols-2">
        {WINDOWS.map((option) => renderWindowButton(option, value === option.id, onSelect))}
      </div>
    </div>
  ),
  BOOKING_STEPS: { id: Step; label: string }[] = [
    { id: "select", label: "Service & time" },
    { id: "details", label: "Details" },
    { id: "phone", label: "Verify" },
    { id: "otp", label: "Code" },
    { id: "profile", label: "Your info" },
    { id: "review", label: "Review" },
    { id: "done", label: "Done" },
  ],

  /** Default wording per step. The form builder edits these (stored as overrides
   *  under root.props.stepText); the widget falls back here when an override is
   *  blank. Steps with dynamic subtitles (otp, done) omit one. */
  BOOKING_STEP_DEFAULTS: Record<Step, { title: string; subtitle?: string }> = {
    details: { subtitle: "Tell us a bit more so we come prepared.", title: "A few details" },
    done: { title: "Request sent." },
    otp: { title: "Enter the code" },
    phone: { subtitle: "We'll text a 6-digit code to confirm your request.", title: "Verify it's you" },
    profile: { subtitle: "Just a name and email so we can reach you.", title: "Your details" },
    review: { subtitle: "They'll confirm the exact time.", title: "Review request" },
    select: { subtitle: "", title: "Book a service" },
  },
  BackLink = ({ onClick }: { onClick: () => void }): React.JSX.Element => (
    <button
      type="button"
      onClick={onClick}
      className="jobsite:mx-auto jobsite:block jobsite:text-sm jobsite:font-medium jobsite:text-muted-foreground jobsite:underline-offset-4 jobsite:hover:text-foreground jobsite:hover:underline"
    >
      Back
    </button>
  ),
  CallButton = ({ phone, label }: { phone: string; label: string }): React.JSX.Element => (
    <Button asChild variant="outline" size="lg" className="jobsite:h-12 jobsite:w-full">
      <a href={phoneHref(phone)}>
        <Phone className="jobsite:h-4 jobsite:w-4" /> {label}
      </a>
    </Button>
  ),
  OtpBoxes = ({ code, setCode }: { code: string; setCode: (value: string) => void }): React.JSX.Element => {
    const ref = React.useRef<HTMLInputElement>(null),
      digits = code.split("");
    React.useEffect(() => {
      const timer = window.setTimeout(() => ref.current?.focus(), OTP_FOCUS_DELAY_MS);
      return (): void => {
        window.clearTimeout(timer);
      };
    }, []);
    return (
      <div
        className="jobsite:relative jobsite:flex jobsite:justify-center jobsite:gap-2"
        onClick={() => ref.current?.focus()}
      >
        {OTP_SLOTS.map((slot) => (
          <div
            key={slot}
            className={cn(
              "jobsite:flex jobsite:h-[56px] jobsite:max-w-[48px] jobsite:flex-1 jobsite:items-center jobsite:justify-center jobsite:rounded-xl jobsite:border-2 jobsite:bg-background jobsite:font-heading jobsite:text-2xl jobsite:text-foreground",
              // Filled boxes plus the one the next digit lands in.
              slot <= code.length && "jobsite:border-primary",
              slot > code.length && "jobsite:border-border",
            )}
          >
            {digits[slot] ?? ""}
          </div>
        ))}
        <input
          ref={ref}
          type="tel"
          inputMode="numeric"
          value={code}
          onChange={(event) => {
            setCode(event.target.value.replace(/\D/g, "").slice(0, OTP_LENGTH));
          }}
          className="jobsite:pointer-events-none jobsite:absolute jobsite:h-px jobsite:w-px jobsite:opacity-0"
        />
      </div>
    );
  },
  /** One sample request-mode service so the preview's picker has something to show. */
  PREVIEW_SERVICES: BookingService[] = [
    {
      blurb: "",
      deposit: null,
      duration: null,
      id: "preview",
      name: "Service request",
      popular: false,
      price: null,
      priceQualifier: null,
      schedulingMode: "request",
      slug: "service-request",
      urgent: false,
    },
  ],
  SummaryRow = ({
    icon,
    label,
    children,
  }: {
    icon: React.ReactNode;
    label: string;
    children: React.ReactNode;
  }): React.JSX.Element => (
    <div className="jobsite:flex jobsite:items-start jobsite:gap-3 jobsite:border-b jobsite:border-border/60 jobsite:py-3 jobsite:last:border-b-0">
      <span className="jobsite:flex jobsite:h-9 jobsite:w-9 jobsite:shrink-0 jobsite:items-center jobsite:justify-center jobsite:rounded-lg jobsite:bg-primary/10 jobsite:text-primary">
        {icon}
      </span>
      <div className="jobsite:min-w-0">
        <div className="jobsite:text-[11px] jobsite:font-semibold jobsite:uppercase jobsite:tracking-[0.04em] jobsite:text-muted-foreground">
          {label}
        </div>
        <div className="jobsite:mt-0.5 jobsite:text-[14.5px] jobsite:font-semibold jobsite:leading-snug jobsite:text-foreground">
          {children}
        </div>
      </div>
    </div>
  ),
  renderCallToBook = (phoneNumber: string): React.JSX.Element => (
    <Button asChild size="lg" className="jobsite:h-12 jobsite:w-full jobsite:font-semibold">
      <a href={phoneHref(phoneNumber)}>
        <Phone className="jobsite:h-4 jobsite:w-4" /> Call to book
      </a>
    </Button>
  ),
  renderOfflineIntro = (title: string, businessName: string | undefined): React.JSX.Element => (
    <div className="jobsite:space-y-1">
      <p className="jobsite:font-heading jobsite:text-xl jobsite:text-card-foreground">
        {title}
      </p>
      <p className="jobsite:text-sm jobsite:text-muted-foreground">
        Online times aren't loading right now. Leave your details and{" "}
        {businessName ?? "we"}'ll call to schedule.
      </p>
    </div>
  ),
  renderDoneIcon = (): React.JSX.Element => (
    <div className="jobsite:mx-auto jobsite:flex jobsite:h-16 jobsite:w-16 jobsite:items-center jobsite:justify-center jobsite:rounded-full jobsite:bg-primary jobsite:text-primary-foreground">
      <MessageSquare className="jobsite:h-7 jobsite:w-7" />
    </div>
  ),
  renderDoneText = (title: string, message: string): React.JSX.Element => (
    <div className="jobsite:space-y-1">
      <p className="jobsite:font-heading jobsite:text-2xl jobsite:text-card-foreground">
        {title}
      </p>
      <p className="jobsite:text-sm jobsite:text-muted-foreground">
        {message}
      </p>
    </div>
  ),
  renderConfirmation = (confirmationCode: string): React.JSX.Element => (
    <div className="jobsite:rounded-xl jobsite:border jobsite:border-border jobsite:bg-muted/40 jobsite:px-4 jobsite:py-3 jobsite:text-sm">
      <span className="jobsite:text-muted-foreground">Confirmation</span>{" "}
      <span className="jobsite:font-mono jobsite:font-semibold jobsite:text-foreground">
        {confirmationCode}
      </span>
    </div>
  ),
  renderStepHeader = (header: { title: string; sub: string }): React.JSX.Element => (
    <div className="jobsite:mb-5 jobsite:space-y-1">
      <p className="jobsite:font-heading jobsite:text-xl jobsite:text-card-foreground">
        {header.title}
      </p>
      {header.sub !== "" && (
        <p className="jobsite:text-sm jobsite:text-muted-foreground">{header.sub}</p>
      )}
    </div>
  ),
  renderSingleService = (service: BookingService): React.JSX.Element => (
    <div className="jobsite:rounded-xl jobsite:border jobsite:border-border jobsite:bg-muted/40 jobsite:px-4 jobsite:py-3">
      <span className="jobsite:text-[15px] jobsite:font-semibold jobsite:text-foreground">
        {service.name}
      </span>
      {priceLabel(service) && (
        <span className="jobsite:text-sm jobsite:text-muted-foreground">
          {" · "}
          {priceLabel(service)}
        </span>
      )}
    </div>
  ),
  renderServiceSelect = (
    services: BookingService[],
    selected: BookingService | null,
    onSelect: (service: BookingService | null) => void,
  ): React.JSX.Element => (
    <div className="jobsite:relative">
      <select
        value={selected?.id ?? ""}
        onChange={(event) =>
          {
          onSelect(services.find((service) => service.id === event.target.value) ?? null);
        }
        }
        className="jobsite:h-12 jobsite:w-full jobsite:appearance-none jobsite:rounded-md jobsite:border jobsite:border-input jobsite:bg-transparent jobsite:pl-3 jobsite:pr-10 jobsite:text-base jobsite:text-foreground jobsite:outline-none jobsite:focus-visible:border-ring jobsite:focus-visible:ring-ring/50 jobsite:focus-visible:ring-[3px]"
      >
        <option value="">Select a service</option>
        {services.map((service) => (
          <option key={service.id} value={service.id}>
            {serviceOptionLabel(service)}
          </option>
        ))}
      </select>
      <ChevronDown className="jobsite:pointer-events-none jobsite:absolute jobsite:right-3 jobsite:top-1/2 jobsite:h-4 jobsite:w-4 jobsite:-translate-y-1/2 jobsite:text-muted-foreground" />
    </div>
  ),
  renderContinue = (disabled: boolean, onClick: () => void): React.JSX.Element => (
    <Button
      type="button"
      size="lg"
      disabled={disabled}
      onClick={onClick}
      className="jobsite:h-12 jobsite:w-full jobsite:font-semibold"
    >
      Continue <ChevronRight className="jobsite:h-4 jobsite:w-4" />
    </Button>
  ),
  renderSmsFinePrint = (sender: string): React.JSX.Element => (
    <span className="jobsite:block jobsite:text-[11px] jobsite:text-muted-foreground">
      Booking confirmations, reminders and replies from{" "}
      {sender}, sent through ServicePro.co. Message
      frequency varies. Msg &amp; data rates may apply. Reply STOP to opt out, HELP for
      help.{" "}
      <a
        href="https://servicepro.co/sms-terms"
        target="_blank"
        rel="noreferrer"
        className="jobsite:underline"
      >
        SMS Terms
      </a>{" "}
      ·{" "}
      <a
        href="https://servicepro.co/privacy-policy"
        target="_blank"
        rel="noreferrer"
        className="jobsite:underline"
      >
        Privacy
      </a>
    </span>
  ),
  /* On the phone step, not after the code, so a carrier reviewer reaches it
     without a phone code. Separate from Send code and never required; the
     wording names who sends, what, how often, rates, STOP, HELP and the two
     links the verification submits (Twilio 30505, SP-664). */
  renderSmsOptIn = (
    checked: boolean,
    onCheckedChange: (checked: boolean) => void,
    sender: string,
  ): React.JSX.Element => (
    <label className="jobsite:flex jobsite:cursor-pointer jobsite:items-start jobsite:gap-2.5">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => {
          onCheckedChange(event.target.checked);
        }}
        className="jobsite:mt-0.5 jobsite:h-[18px] jobsite:w-[18px] jobsite:shrink-0 jobsite:accent-primary"
      />
      <span className="jobsite:text-[12px] jobsite:leading-relaxed">
        Text me updates about this booking{" "}
        <span className="jobsite:text-muted-foreground">(optional)</span>
        {renderSmsFinePrint(sender)}
      </span>
    </label>
  ),
  // Only the code the person just asked for; booking texts are the opt-in box.
  renderOtpFinePrint = (): React.JSX.Element => (
    <p className="jobsite:text-center jobsite:text-[11px] jobsite:leading-relaxed jobsite:text-muted-foreground">
      We&apos;ll text you a one-time code. Msg &amp; data rates may apply. See{" "}
      <a
        href="https://servicepro.co/sms-terms"
        target="_blank"
        rel="noreferrer"
        className="jobsite:underline"
      >
        SMS Terms
      </a>{" "}
      &amp;{" "}
      <a
        href="https://servicepro.co/privacy-policy"
        target="_blank"
        rel="noreferrer"
        className="jobsite:underline"
      >
        Privacy
      </a>
      .
    </p>
  ),
  renderRequestFinePrint = (): React.JSX.Element => (
    <p className="jobsite:text-center jobsite:text-[11px] jobsite:leading-relaxed jobsite:text-muted-foreground">
      By sending this request, you agree to ServicePro's{" "}
      <a
        href="https://servicepro.co/terms-of-service"
        target="_blank"
        rel="noreferrer"
        className="jobsite:underline"
      >
        Terms
      </a>{" "}
      &amp;{" "}
      <a
        href="https://servicepro.co/privacy-policy"
        target="_blank"
        rel="noreferrer"
        className="jobsite:underline"
      >
        Privacy
      </a>
      .
    </p>
  ),
  renderSectionHeading = (heading: string | undefined, subheading: string | undefined): React.ReactNode => {
    if (!hasText(heading) && !hasText(subheading)) {
      return null;
    }
    return (
      <div className="jobsite:mb-8 jobsite:text-center">
        {hasText(heading) && (
          <h2 className="jobsite:font-heading jobsite:text-4xl jobsite:text-foreground jobsite:md:text-5xl">
            {heading}
          </h2>
        )}
        {hasText(subheading) && (
          <p className="jobsite:mt-4 jobsite:text-lg jobsite:text-muted-foreground jobsite:md:text-xl">
            {subheading}
          </p>
        )}
      </div>
    );
  },

  renderBookingColumn = (
    heading: string | undefined,
    subheading: string | undefined,
    widget: React.ReactNode,
  ): React.JSX.Element => (
    <div className="jobsite:mx-auto jobsite:max-w-xl">
      {renderSectionHeading(heading, subheading)}
      {widget}
    </div>
  ),

  /* ──────────────────── the inline flow card ──────────────────── */

  BookingWidgetInline: React.FC<BookingWidgetInlineProps> = ({
    slug,
    apiUrl,
    businessName,
    businessPhone,
    title = "Book a service",
    subtitle = "",
    className,
    preview,
    editor,
    renderFields,
    step: controlledStep,
    onStepChange,
  }) => {
    const [load, setLoad] = React.useState<LoadState>("loading"),
      [pro, setPro] = React.useState<BookingPro | null>(null),
      [services, setServices] = React.useState<BookingService[]>([]),
      [formData, setFormData] = React.useState<Data>(EMPTY_FORM_DATA),
      days = React.useMemo(buildRequestDays, []),
      // Where a request goes when the API can't take it (SP-400); absent → no fallback.
      { backupApiUrl, siteId } = getWindowJobsiteConfig(),

      // Selection
      [svc, setSvc] = React.useState<BookingService | null>(null),
      [dayIso, setDayIso] = React.useState(days[0].iso),
      [win, setWin] = React.useState<WindowId | null>(null),
      [note, setNote] = React.useState(""),
      // Custom (form-builder) field values, keyed by field.key → formData on submit.
      [customValues, setCustomValues] = React.useState<Record<string, FieldValue>>({}),

      // Identity
      // Editor mode lets the form builder drive navigation; live mode self-manages.
      [stepState, setStepState] = React.useState<Step>("select"),
      setStep = (next: Step): void => {
        onStepChange?.(next);
        if (controlledStep === undefined) {
          setStepState(next);
        }
      },
      step = controlledStep ?? stepState,
      [phone, setPhone] = React.useState(""),
      [smsOptIn, setSmsOptIn] = React.useState(false),
      [code, setCode] = React.useState(""),
      [verification, setVerification] = React.useState<{
        id: string;
        customer: VerifiedCustomer;
      } | null>(null),
      [profile, setProfile] = React.useState({ email: "", first: "", last: "" }),
      [termsConsent, setTermsConsent] = React.useState(false),

      // Async ui
      [busy, setBusy] = React.useState(false),
      [err, setErr] = React.useState<string>(),
      [confCode, setConfCode] = React.useState(""),
      // SP-400: this became a lead for a human to schedule, not a confirmed booking.
      [parked, setParked] = React.useState(false),
      [offlineForm, setOfflineForm] = React.useState({
        email: "",
        name: "",
        need: "",
        phone: "",
        when: "",
      }),

      // Bot protection — reuses the jobsite's per-site Turnstile (same key the
      // Contact form uses). No-op when the site has no Turnstile configured.
      {
        tokenRef: turnstileTokenRef,
        containerRef: turnstileContainerRef,
        containerClassName: turnstileClassName,
      } = useTurnstile(),

      known = verification?.customer.exists ?? false,
      name = displayName(verification?.customer, profile),
      windowLabel = WINDOWS.find((option) => option.id === win)?.label ?? "",
      dayLabel = days.find((day) => day.iso === dayIso)?.label ?? "",
      phoneNumber = businessPhone ?? pro?.phone ?? null,

      /**
       * Hold a request for a human, retrying the primary in case it has recovered.
       * @param {ParkedBookingInput} input - What the visitor asked for.
       * @returns {Promise<boolean>} True when the request was held (or taken), false when it went nowhere.
       */
      park = async (input: ParkedBookingInput): Promise<boolean> => {
        if (!hasText(apiUrl) || !hasText(siteId) || !hasText(backupApiUrl)) {
          return false;
        }
        // Unspent: sendOtp is the only step that consumes a token, and parking it
        // Means that call never reached a server that could have validated one.
        const turnstileToken = turnstileTokenRef.current,
          result = await submitLead({
            apiUrl,
            backupApiUrl,
            businessName: businessName ?? pro?.name ?? undefined,
            payload: {
              ...parkedBookingPayload({ ...input, sessionId: currentSessionId() }),
              ...turnstileField(turnstileToken),
            },
            siteId,
          });
        if (result.status === "failed") {
          return false;
        }
        // True even when the primary took it: nothing here scheduled a time.
        setParked(true);
        setStep("done");
        return true;
      },

      /**
       * Everything the visitor has given the flow so far, as a lead.
       * @returns {Promise<boolean>} True when the request was held, false when it went nowhere.
       */
      parkFromFlow = async (): Promise<boolean> => {
        const labels = new Map(fieldsFromData(formData).map((field) => [field.id, field.label]));
        return park({
          details: Object.entries(customValues).map(([id, value]) => ({
            label: labels.get(id) ?? id,
            value: fieldValueText(value),
          })),
          email: profile.email || undefined,
          name: name || undefined,
          note,
          phone: toE164(phone) ?? phone,
          preferredTime: [dayLabel, windowLabel].filter(Boolean).join(" · "),
          service: svc?.name,
        });
      },

      confirm = async (): Promise<void> => {
        if (editor === true) {
          setConfCode("ABC123");
          setStep("done");
          return;
        }
        if (!verification || !svc) {
          return;
        }
        setBusy(true);
        setErr(undefined);
        try {
          const res = await createBooking(apiUrl!, {
            consent: known || termsConsent,
            customFields: customFieldsToSend(customValues),
            email: profile.email || undefined,
            firstName: profile.first || undefined,
            lastName: profile.last || undefined,
            request: { note: note.trim() || undefined, preferredDate: dayIso, window: win ?? "asap" },
            serviceId: svc.id,
            sessionId: currentSessionId(),
            slug: slug!,
            smsOptIn,
            verificationId: verification.id,
          });
          if (res.ok) {
            setConfCode(res.booking.confCode);
            setStep("done");
            return;
          }
          if (res.offline && (await parkFromFlow())) {
            return;
          }
          setErr(res.error);
        } finally {
          setBusy(false);
        }
      },
      send = async (): Promise<void> => {
        if (editor === true) {
          setStep("otp");
          return;
        }
        // If the site enforces Turnstile, require a solved token before spending SMS.
        if (hasText(getWindowJobsiteConfig()?.turnstileSiteKey) && !hasText(turnstileTokenRef.current)) {
          setErr("Please complete the verification check and try again.");
          return;
        }
        setBusy(true);
        setErr(undefined);
        try {
          const res = await sendOtp(apiUrl!, toE164(phone) ?? phone, {
            slug,
            turnstileToken: turnstileTokenRef.current,
          });
          if (res.ok) {
            setCode("");
            setStep("otp");
            return;
          }
          if (res.offline && (await parkFromFlow())) {
            return;
          }
          setErr(res.error);
        } finally {
          setBusy(false);
        }
      },
      verify = async (): Promise<void> => {
        if (editor === true) {
          setStep("review");
          return;
        }
        setBusy(true);
        setErr(undefined);
        try {
          const res = await verifyOtp(apiUrl!, toE164(phone) ?? phone, code);
          if (!res.ok) {
            if (res.offline && (await parkFromFlow())) {
              return;
            }
            setErr(res.error);
            return;
          }
          setVerification({ customer: res.customer, id: res.verificationId });
          if (res.customer.exists) {
            setProfile({
              email: res.customer.email ?? "",
              first: res.customer.firstName ?? "",
              last: res.customer.lastName ?? "",
            });
            setStep("review");
          } else {
            setStep("profile");
          }
        } finally {
          setBusy(false);
        }
      },

      renderDoneFooter = (): React.ReactNode => {
        if (!parked) {
          return renderConfirmation(orSample(confCode, editor, "ABC123"));
        }
        return hasText(phoneNumber) && <CallButton phone={phoneNumber} label={`Call ${businessName ?? "us"}`} />;
      },

      /* ── header (varies by step; per-step overrides win, else the default) ── */
      renderHeader = (current: Exclude<Step, "done">): React.JSX.Element => {
        const stepText = stepTextFromData(formData),
          headerByStep: Record<Exclude<Step, "done">, { title: string; sub: string }> = {
            details: {
              sub: overrideSubtitle(stepText, "details", BOOKING_STEP_DEFAULTS.details.subtitle!),
              title: overrideTitle(stepText, "details", BOOKING_STEP_DEFAULTS.details.title),
            },
            otp: {
              sub: `Sent to ${orSample(phone, editor, "(512) 555-0148")}.`,
              title: overrideTitle(stepText, "otp", BOOKING_STEP_DEFAULTS.otp.title),
            },
            phone: {
              sub: overrideSubtitle(stepText, "phone", BOOKING_STEP_DEFAULTS.phone.subtitle!),
              title: overrideTitle(stepText, "phone", BOOKING_STEP_DEFAULTS.phone.title),
            },
            profile: {
              sub: overrideSubtitle(stepText, "profile", BOOKING_STEP_DEFAULTS.profile.subtitle!),
              title: overrideTitle(stepText, "profile", BOOKING_STEP_DEFAULTS.profile.title),
            },
            review: {
              sub: overrideSubtitle(stepText, "review", BOOKING_STEP_DEFAULTS.review.subtitle!),
              title: overrideTitle(stepText, "review", BOOKING_STEP_DEFAULTS.review.title),
            },
            select: { sub: overrideSubtitle(stepText, "select", subtitle), title: overrideTitle(stepText, "select", title) },
          };
        return renderStepHeader(headerByStep[current]);
      },

      renderServicePicker = (): React.JSX.Element => {
        if (services.length === 1) {
          return renderSingleService(services[0]);
        }
        return renderServiceSelect(services, svc, setSvc);
      },

      /* ── SELECT: service + day + window + note ── */
      renderSelect = (): React.JSX.Element => {
        const noteConfig = noteConfigFromData(formData),
          noteMissing = noteConfig.enabled && noteConfig.required && !note.trim();
        return (
          <div className="jobsite:space-y-5">
            <div className="jobsite:space-y-2">
              <div className={UPPER_LABEL}>Service</div>
              {renderServicePicker()}
            </div>

            {/* Day + time always show — request-mode has no availability gate, so
                the picker is the inviting default rather than an empty shell. */}
            <DayRail days={days} selectedIso={dayIso} onSelect={setDayIso} />
            <WindowGrid value={win} onSelect={setWin} />
            {noteConfig.enabled && (
              <Textarea
                value={note}
                onChange={(event) => {
                  setNote(event.target.value);
                }}
                rows={2}
                placeholder={notePlaceholder(noteConfig.required, noteConfig.label)}
                className="jobsite:resize-none jobsite:text-sm"
              />
            )}
            {renderContinue(editor !== true && (!svc || !win || noteMissing), () => {
              setStep(afterSelectStep(formData));
            })}
          </div>
        );
      },

      renderDetailFields = (): React.ReactNode => {
        if (renderFields) {
          return renderFields();
        }
        return (
          <FormValuesProvider
            values={customValues}
            onChange={(id, value) => {
              setCustomValues((prev) => ({ ...prev, [id]: value }));
            }}
            uploadImage={async (file) => {
              const res = await uploadFormImage(apiUrl!, slug!, file);
              if (res.ok) {
                return res.url;
              }
              return null;
            }}
          >
            <Render config={formsPuckConfig} data={formData} />
          </FormValuesProvider>
        );
      },

      /* ── DETAILS (form-builder fields) — the form's Puck document rendered
          by the same config the editor uses; FormValuesProvider collects. ── */
      renderDetails = (): React.JSX.Element => {
        const detailsComplete = missingRequiredFromData(formData, customValues).length === 0;
        return (
          <div className="jobsite:space-y-4">
            {renderDetailFields()}
            <div className="jobsite:space-y-3">
              {renderContinue(editor !== true && !detailsComplete, () => {
                setStep("phone");
              })}
              <BackLink onClick={() => {
                setStep("select");
              }} />
            </div>
          </div>
        );
      },

      /* ── PHONE ── */
      renderPhone = (): React.JSX.Element => (
        <div className="jobsite:space-y-4">
          <Input
            type="tel"
            inputMode="numeric"
            autoFocus
            placeholder="(512) 555-0148"
            value={phone}
            onChange={(event) => {
              setPhone(nextPhoneInput(event.target.value, phone));
            }}
            className="jobsite:h-12 jobsite:text-base"
          />
          {renderSmsOptIn(smsOptIn, setSmsOptIn, businessName ?? pro?.name ?? "this business")}
          {/* Turnstile renders here when the site has a key (CF managed mode). */}
          <div ref={turnstileContainerRef} className={turnstileClassName} />
          {hasText(err) && <p className="jobsite:text-sm jobsite:font-medium jobsite:text-destructive">{err}</p>}
          <div className="jobsite:space-y-3">
            <Button
              type="button"
              size="lg"
              disabled={editor !== true && (toE164(phone) === null || busy)}
              onClick={send}
              className="jobsite:h-12 jobsite:w-full jobsite:font-semibold"
            >
              {busyLabel(busy, "Send code")}
            </Button>
            <BackLink onClick={() => {
              setStep(beforePhoneStep(formData));
            }} />
          </div>
          {renderOtpFinePrint()}
        </div>
      ),

      /* ── OTP ── */
      renderOtp = (): React.JSX.Element => (
        <div className="jobsite:space-y-4">
          <OtpBoxes code={code} setCode={setCode} />
          {hasText(err) && (
            <p className="jobsite:text-center jobsite:text-sm jobsite:font-medium jobsite:text-destructive">
              {err}
            </p>
          )}
          <div className="jobsite:space-y-3">
            <Button
              type="button"
              size="lg"
              disabled={editor !== true && (code.length !== OTP_LENGTH || busy)}
              onClick={verify}
              className="jobsite:h-12 jobsite:w-full jobsite:font-semibold"
            >
              {busyLabel(busy, "Verify")}
            </Button>
            <BackLink onClick={() => {
              setStep("phone");
            }} />
          </div>
        </div>
      ),

      renderNameInputs = (): React.JSX.Element => (
        <div className="jobsite:grid jobsite:grid-cols-2 jobsite:gap-3">
          <Input
            autoFocus
            placeholder="First name"
            value={profile.first}
            onChange={(event) => {
              setProfile({ ...profile, first: event.target.value });
            }}
            className="jobsite:h-12 jobsite:text-base"
          />
          <Input
            placeholder="Last name"
            value={profile.last}
            onChange={(event) => {
              setProfile({ ...profile, last: event.target.value });
            }}
            className="jobsite:h-12 jobsite:text-base"
          />
        </div>
      ),

      /* ── PROFILE (new customers) ── */
      renderProfile = (): React.JSX.Element => (
        <div className="jobsite:space-y-4">
          {renderNameInputs()}
          <Input
            type="email"
            inputMode="email"
            placeholder="Email for your receipt"
            value={profile.email}
            onChange={(event) => {
              setProfile({ ...profile, email: event.target.value });
            }}
            className="jobsite:h-12 jobsite:text-base"
          />
          <label className="jobsite:flex jobsite:cursor-pointer jobsite:items-start jobsite:gap-2.5">
            <input
              type="checkbox"
              checked={termsConsent}
              onChange={(event) => {
                setTermsConsent(event.target.checked);
              }}
              className="jobsite:mt-0.5 jobsite:h-[18px] jobsite:w-[18px] jobsite:shrink-0 jobsite:accent-primary"
            />
            <span className="jobsite:text-[12px] jobsite:leading-relaxed jobsite:text-muted-foreground">
              I agree to the Terms &amp; Cancellation policy.
            </span>
          </label>
          <Button
            type="button"
            size="lg"
            disabled={
              editor !== true &&
              (!profile.first.trim() ||
                !profile.last.trim() ||
                !/\S+@\S+\.\S+/.test(profile.email) ||
                !termsConsent)
            }
            onClick={() => {
              setStep("review");
            }}
            className="jobsite:h-12 jobsite:w-full jobsite:font-semibold"
          >
            Review request
          </Button>
        </div>
      ),

      renderSummary = (service: BookingService): React.JSX.Element => (
        <div className="jobsite:rounded-xl jobsite:border jobsite:border-border jobsite:px-4">
          <SummaryRow icon={<Check className="jobsite:h-4 jobsite:w-4" />} label="Service">
            {service.name}
          </SummaryRow>
          <SummaryRow icon={<Calendar className="jobsite:h-4 jobsite:w-4" />} label="Preferred time">
            {dayLabel} · {orSample(windowLabel, editor, "Morning")}
          </SummaryRow>
          <SummaryRow icon={<Clock className="jobsite:h-4 jobsite:w-4" />} label="Requested by">
            {orSample(name, editor, "Alex Rivera") || "—"}
            <span className="jobsite:font-normal jobsite:text-muted-foreground">
              {" · "}
              {orSample(phone, editor, "(512) 555-0148")}
            </span>
          </SummaryRow>
          {note.trim() && (
            <SummaryRow icon={<MessageSquare className="jobsite:h-4 jobsite:w-4" />} label="Note">
              <span className="jobsite:font-normal">{note.trim()}</span>
            </SummaryRow>
          )}
        </div>
      ),

      /* ── REVIEW ── */
      renderReview = (service: BookingService): React.JSX.Element => (
        <div className="jobsite:space-y-4">
          {renderSummary(service)}
          {hasText(err) && <p className="jobsite:text-sm jobsite:font-medium jobsite:text-destructive">{err}</p>}
          <div className="jobsite:space-y-3">
            <Button
              type="button"
              size="lg"
              disabled={editor !== true && busy}
              onClick={confirm}
              className="jobsite:h-12 jobsite:w-full jobsite:font-semibold"
            >
              {busyLabel(busy, "Send request")}
            </Button>
            <BackLink onClick={() => {
              setStep(reviewBackStep(known));
            }} />
          </div>
          {renderRequestFinePrint()}
        </div>
      );

    React.useEffect(() => {
      let alive = true;
      // Preview (form builder): seed from the draft, skip the network entirely.
      if (preview) {
        const svcs = preview.services ?? PREVIEW_SERVICES;
        setPro({ area: null, name: businessName ?? "Your business", phone: null, slug: "", trade: null });
        setServices(svcs);
        if (svcs.length === 1) {
          setSvc(svcs[0]);
        }
        setLoad("ready");
        return;
      }
      if (!hasText(slug) || !hasText(apiUrl)) {
        setLoad("unavailable");
        return;
      }
      void fetchBookingPublic(apiUrl, slug).then((res) => {
        if (!alive) {
          return;
        }
        if (!res.ok) {
          setLoad(failedLoadState(res.offline, hasText(backupApiUrl) && hasText(siteId)));
          return;
        }
        // Only request-mode services work here: the server derives instant vs
        // Request from the service config, and instant needs real availability
        // (not built). Instant services route to the full booking page instead.
        const requestable = res.data.services.filter((service) => service.schedulingMode === "request");
        if (requestable.length === 0) {
          setLoad("unavailable");
          return;
        }
        setPro(res.data.pro);
        setServices(requestable);
        setFormData(res.data.formData);
        if (requestable.length === 1) {
          setSvc(requestable[0]);
        }
        setLoad("ready");
      });
      return (): void => {
        alive = false;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug, apiUrl]);

    // Keep the preview form in sync with the live draft, without resetting the
    // Flow (selection/step) the way a full reseed would.
    React.useEffect(() => {
      if (preview) {
        setFormData(preview.formData);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preview?.formData]);

    /* ── loading / unavailable fallbacks ── */
    if (load === "loading") {
      // Form-shaped silhouette (≈ the picker step's height) so the widget doesn't
      // Jump when the real content arrives.
      return (
        <Card className={cn("jobsite:border jobsite:shadow-xl", className)}>
          <CardContent className="jobsite:p-6 jobsite:sm:p-8">
            <FormEmbedSkeleton fields={3} />
          </CardContent>
        </Card>
      );
    }
    if (load === "unavailable") {
      // No bookable services — fall back to a call CTA so the slot is never dead.
      return (
        <Card className={cn("jobsite:border jobsite:shadow-xl", className)}>
          <CardContent className="jobsite:space-y-3 jobsite:p-6 jobsite:text-center">
            <p className="jobsite:font-heading jobsite:text-xl jobsite:text-card-foreground">
              {title}
            </p>
            <p className="jobsite:text-sm jobsite:text-muted-foreground">
              {unavailableMessage(hasText(phoneNumber))}
            </p>
            {hasText(phoneNumber) && renderCallToBook(phoneNumber)}
          </CardContent>
        </Card>
      );
    }

    // No services and no times to offer, so ask in free text; a call CTA alone loses everyone who won't phone.
    if (load === "offline" && !parked) {
      const ready =
        offlineForm.name.trim() !== "" &&
        toE164(offlineForm.phone) !== null &&
        offlineForm.need.trim() !== "";
      return (
        <Card className={cn("jobsite:border jobsite:shadow-xl", className)}>
          <CardContent className="jobsite:space-y-4 jobsite:p-6 jobsite:sm:p-8">
            {renderOfflineIntro(title, businessName)}
            <Input
              placeholder="Your name"
              autoComplete="name"
              value={offlineForm.name}
              onChange={(event) => {
                setOfflineForm((form) => ({ ...form, name: event.target.value }));
              }}
              className="jobsite:h-12 jobsite:text-base"
            />
            <Input
              type="tel"
              inputMode="numeric"
              autoComplete="tel"
              placeholder="(512) 555-0148"
              value={offlineForm.phone}
              onChange={(event) => {
                const typed = event.target.value;
                setOfflineForm((form) => ({ ...form, phone: nextPhoneInput(typed, form.phone) }));
              }}
              className="jobsite:h-12 jobsite:text-base"
            />
            <Input
              type="email"
              autoComplete="email"
              placeholder="Email (optional)"
              value={offlineForm.email}
              onChange={(event) => {
                setOfflineForm((form) => ({ ...form, email: event.target.value }));
              }}
              className="jobsite:h-12 jobsite:text-base"
            />
            <Textarea
              placeholder="What do you need done?"
              value={offlineForm.need}
              onChange={(event) => {
                setOfflineForm((form) => ({ ...form, need: event.target.value }));
              }}
              className="jobsite:min-h-24 jobsite:text-base"
            />
            <Input
              placeholder="When works for you? (e.g. weekday mornings)"
              value={offlineForm.when}
              onChange={(event) => {
                setOfflineForm((form) => ({ ...form, when: event.target.value }));
              }}
              className="jobsite:h-12 jobsite:text-base"
            />
            <div ref={turnstileContainerRef} className={turnstileClassName} />
            {hasText(err) && <p className="jobsite:text-sm jobsite:font-medium jobsite:text-destructive">{err}</p>}
            <Button
              type="button"
              size="lg"
              disabled={!ready || busy}
              onClick={async () => {
                setBusy(true);
                setErr(undefined);
                const sent = await park({
                  email: offlineForm.email,
                  name: offlineForm.name,
                  note: offlineForm.need,
                  phone: toE164(offlineForm.phone) ?? offlineForm.phone,
                  preferredTime: offlineForm.when,
                });
                setBusy(false);
                if (!sent) {
                  setErr(sendFailedMessage(phoneNumber));
                }
              }}
              className="jobsite:h-12 jobsite:w-full jobsite:font-semibold"
            >
              {busyLabel(busy, "Send request")}
            </Button>
            {hasText(phoneNumber) && <CallButton phone={phoneNumber} label="Call instead" />}
          </CardContent>
        </Card>
      );
    }

    /* ── success ── */
    if (step === "done") {
      return (
        <Card className={cn("jobsite:border jobsite:shadow-xl", className)}>
          <CardContent className="jobsite:space-y-4 jobsite:p-6 jobsite:text-center">
            {renderDoneIcon()}
            {renderDoneText(
              doneTitle(parked, formData),
              doneMessage(parked, businessName ?? pro?.name ?? "We", profile.first),
            )}
            {renderDoneFooter()}
          </CardContent>
        </Card>
      );
    }

    return (
      <Card className={cn("jobsite:overflow-hidden jobsite:border jobsite:shadow-xl", className)}>
        <CardContent className="jobsite:p-6 jobsite:sm:p-7">
          {renderHeader(step)}
          {step === "select" && renderSelect()}
          {step === "details" && renderDetails()}
          {step === "phone" && renderPhone()}
          {step === "otp" && renderOtp()}
          {step === "profile" && renderProfile()}
          {step === "review" && svc && renderReview(svc)}
        </CardContent>
      </Card>
    );
  },

  /* ──────────────────── the standalone section ──────────────────── */

  BookingWidget: React.FC<BookingWidgetProps> = ({
    id,
    heading,
    subheading,
    slug,
    apiUrl,
    businessName,
    businessPhone,
    backgroundColor,
    className,
  }) => {
    // Resolve runtime config at render for the live jobsite (props win when the
    // Puck config supplied them from metadata).
    const config = getWindowJobsiteConfig(),
      business = config.business as JobsiteBusinessContext | undefined,
      resolvedApiUrl = apiUrl ?? config.apiUrl,
      resolvedSlug = slug ?? business?.slug,
      widget = (
        <BookingWidgetInline
          slug={resolvedSlug}
          apiUrl={resolvedApiUrl}
          businessName={businessName ?? business?.name}
          businessPhone={businessPhone ?? business?.phone}
        />
      );

    return (
      <section
        id={(id ?? "") || "book"}
        className={cn("jobsite:py-16 jobsite:md:py-24 jobsite:bg-background", className)}
        style={{ backgroundColor: (backgroundColor ?? "") || undefined }}
      >
        <div className="jobsite:container jobsite:mx-auto jobsite:px-4">
          {renderBookingColumn(heading, subheading, widget)}
        </div>
      </section>
    );
  };

export type { BookingWidgetInlineProps, BookingStep, BookingWidgetProps };
export { BOOKING_STEPS, BOOKING_STEP_DEFAULTS, BookingWidgetInline, BookingWidget };
